import { Checkbox, Chip, Progress, Spinner } from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, QrCode, ScanText, Trash2, Upload } from "lucide-react";
import { useState } from "react";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { errorMessage } from "@/lib/api";
import { showError, showSuccess } from "@/lib/toast";
import DrawingCard from "./DrawingCard";
import DrawingDetailDialog from "./DrawingDetailDialog";
import DrawingImportDialog from "./DrawingImportDialog";
import DrawingMatchDialog from "./DrawingMatchDialog";
import QrAreaDialog from "./QrAreaDialog";
import type { DrawingsPanelOps, PanelDrawing, PanelItem, QrStampArea } from "./types";
import { useDrawingEvents } from "./useDrawingEvents";

/**
 * 一覧の絞り込み。**手を入れる必要がある図面を拾う**ためのもの。
 *
 * 42 枚並ぶと、どれが未処理なのか絵からは分からない。並べ替えではなく絞り込みに
 * しているのは、「残り何件か」を数えられるようにするため（チップに件数を出す）。
 *
 * 複数を組み合わせられるようにはしない。条件が増えるほど「0 件になったのは
 * なぜか」が読めなくなる。
 */
const FILTERS = [
  { key: "all", label: "すべて", match: () => true },
  {
    key: "unmatched",
    label: "紐付無し",
    match: (d: PanelDrawing) => d.item_id == null,
  },
  {
    key: "no-number",
    label: "図番なし",
    match: (d: PanelDrawing) => !d.drawing_number,
  },
  {
    key: "needs-check",
    label: "OCR要確認",
    // 採用しなかった読み（low）と、採用はしたが 2 位が近い読み（medium）。
    match: (d: PanelDrawing) =>
      d.ocr_confidence === "low" || d.ocr_confidence === "medium",
  },
  {
    key: "no-thumbnail",
    label: "サムネイルなし",
    match: (d: PanelDrawing) => d.thumbnail_status !== "ready",
  },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

/**
 * 見積・案件の「図面」タブの中身。**届いた図面 PDF をまとめて取り込む**。
 *
 * 見積と案件で画面は同じものを使い、**経路と文言だけを `ops` で差し替える**
 * （[[types]] の `DrawingsPanelOps`）。やることが同じだからで、見積は客先から
 * 届いた図面、案件は受注後に届いた追図・差し替えを扱う。
 *
 * 図面と明細は別々に届く。明細は依頼の Excel や注文書から起こし、図面 PDF は
 * 別便でまとめて来ることが多い。そのため図面は一旦どの明細にも結び付かない状態で
 * 入り、**図番で突き合わせて**明細に紐付ける。
 *
 * 一覧をカードのグリッドにしているのは、**どの図面がまだ付いていないかを絵で
 * 探す**ための画面だから。図番の文字列だけ並べても手元の紙と突き合わせられない。
 * 表ではないので表示列の設定は置いていない。
 *
 * **1 ファイルに複数ページ分の図面が入っていることがある**（依頼 1 件ぶんを
 * まとめて送ってくる）。1 ページ = 図面 1 枚なので、サーバー側でページに分けて
 * それぞれを図面として登録する。画面からは「1 ファイル上げたら N 枚増えた」と
 * 見える。
 *
 * 図番は取り込んだあとにサーバーが PDF から読み取る（OCR）。読めなかったときの
 * 受け皿としてファイル名からの推測が入るが、**どちらもサーバー側で決める**ので
 * 画面はファイル名を渡すだけでよい。
 *
 * **突き合わせはサーバー側で自動でも走る**（[[drawing_auto_link]]）。図番を読み
 * 終えた図面と、明細が増えた案件の図面が対象で、図番が揃った時点で付く。この
 * ボタンが残っているのは、自動では決まらなかったぶん——図番を直したあと、同じ
 * 図番の明細が複数あって選べなかったぶん——を人がまとめて片付けるため。
 */
export default function DrawingsPanel({
  ops,
  canWrite,
  items,
}: {
  /** 持ち主（見積・案件）ごとの経路と文言。 */
  ops: DrawingsPanelOps;
  canWrite: boolean;
  /** 紐付け先の候補にする明細。 */
  items: PanelItem[];
}) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [openedId, setOpenedId] = useState<number | null>(null);
  /**
   * 一括操作の対象（図面 ID）。
   *
   * 実体ではなく ID で持つ。取得し直すと行の実体は入れ替わるが、選択は残したい。
   */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [deleting, setDeleting] = useState<PanelDrawing | null>(null);
  /** 明細と紐づけのダイアログを開いているか。 */
  const [matching, setMatching] = useState(false);
  /** QR を押す区画を選ぶダイアログを開いているか（まとめて押すとき）。 */
  const [choosingQrArea, setChoosingQrArea] = useState(false);
  const drawings = useQuery({
    queryKey: ops.queryKey,
    queryFn: () => ops.list(),
  });

  /**
   * 後処理の進み具合をサーバから受け取る。
   *
   * 以前はここで 2 秒おきに取得し直していた。出来ていても最大 2 秒遅れるうえ、
   * 「60 秒だけ問い合わせる」という決め打ちが要り、42 ページの取り込みでは
   * 終わる前に窓が閉じて「未生成」のまま残っていた。知らせを受けてから取り直す
   * 形にすれば、どちらも起きない。
   */
  const pending = useDrawingEvents(ops.eventsPath, () => {
    void queryClient.invalidateQueries({ queryKey: ops.queryKey });
  });
  const rows = drawings.data?.items ?? [];
  // 開いているカードは ID で覚える。実体を持つと、取得し直したあとも古い値
  // （サムネイルの URL や紐付け先）を映したままになる。
  const opened = rows.find((d) => d.drawing_id === openedId) ?? null;

  const activeFilter = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
  /** 絞り込みを通した行。以降の「選択」「件数」はすべてこちらを見る。 */
  const visible = rows.filter(activeFilter.match);

  /**
   * いま画面に見えている図面に絞った選択。
   *
   * **見えていないものは操作の対象にしない**。削除や取り込みで行が入れ替わるし、
   * 絞り込みを変えれば見える範囲も変わる。残したまま数えると「3 件選択中」なのに
   * 2 件しか動かない、という食い違いが出る。選択そのものは ID で持っているので、
   * 絞り込みを戻せば選び直さずに済む。
   */
  const selected = visible.filter((d) => selectedIds.has(d.drawing_id));
  const selectedCount = selected.length;
  const allSelected = visible.length > 0 && selectedCount === visible.length;
  /** 選択中かどうか。カードを押したときの動き（開く／選ぶ）が変わる。 */
  const selecting = selectedCount > 0;

  const toggleSelect = (drawing: PanelDrawing) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(drawing.drawing_id)) next.delete(drawing.drawing_id);
      else next.add(drawing.drawing_id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(visible.map((d) => d.drawing_id)));
  const clearSelection = () => setSelectedIds(new Set());

  /** 一括操作の対象。**選んだものだけ**に効く。 */
  const targetIds = selected.map((d) => d.drawing_id);

  /**
   * 紐付け先に出す明細。**既に図面が付いている行は外す**。
   *
   * 1 つの明細に 2 枚の図面を付けることは無いので、候補に残すと「選んだ瞬間に
   * 前の図面が外れる」操作ができてしまう。開いている図面自身の紐付け先は、
   * いま選ばれている値として出す必要があるので残す。
   */
  const linkableItems = (() => {
    const taken = new Set(
      rows
        .filter((d) => d.item_id != null && d.drawing_id !== openedId)
        .map((d) => d.item_id),
    );
    return items.filter((item) => !taken.has(item.item_id));
  })();

  /** 図面を変えると明細の版数・紐付けも変わるので、明細一覧も引き直す。 */
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ops.queryKey }),
      ...ops.invalidateKeys.map((key) =>
        queryClient.invalidateQueries({ queryKey: key }),
      ),
    ]);
  };

  /**
   * 選ばれた PDF を 1 ファイルずつ取り込む。
   *
   * 1 ファイルが複数ページなら、サーバー側でページに分かれて複数の図面になる。
   * そのため「ファイル数」と「増える図面の数」は一致しない。進み具合はファイル
   * 単位で出し、結果は図面の数で知らせる。
   *
   * 並列に投げない。S3 への PUT が同時に走ると回線を取り合って体感が落ちるうえ、
   * 途中で失敗したときにどこまで入ったか分からなくなる。失敗しても後続は続け、
   * 最後にまとめて知らせる（1 件のつまずきで全部やり直しにしない）。
   */
  const upload = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const failed: string[] = [];
    let created = 0;
    for (const [index, file] of files.entries()) {
      try {
        const result = await ops.importFile(file);
        created += result.created_count;
      } catch (e) {
        failed.push(`${file.name}（${errorMessage(e, "原因不明のエラー")}）`);
      }
      setProgress({ done: index + 1, total: files.length });
    }
    await invalidate();
    setUploading(false);
    setProgress(null);
    if (failed.length > 0) {
      showError(`取り込めなかったファイルがあります: ${failed.join(" / ")}`);
    } else {
      // ページに分かれるので、ファイル数ではなく図面の数で伝える。
      showSuccess(
        files.length === 1
          ? `${created} 枚の図面を取り込みました。`
          : `${files.length} ファイルから ${created} 枚の図面を取り込みました。`,
      );
    }
  };

  const rebuild = useMutation({
    mutationFn: () => ops.rebuildThumbnails(),
    onSuccess: async () => {
      await invalidate();
      showSuccess("サムネイルを作り直しています。");
    },
    onError: (e: Error) => showError(e.message),
  });

  const reread = useMutation({
    mutationFn: () => ops.rereadNumbers(targetIds),
    onSuccess: async () => {
      await invalidate();
      showSuccess("図番を読み直しています。");
    },
    onError: (e: Error) => showError(e.message),
  });

  /**
   * 選んだ図面へまとめて QR を押す（依頼するだけ）。
   *
   * **飛ばしたぶんも言う。** 選択の中に明細未紐付けや押印済みが混ざるのは普通の
   * ことで、黙って飛ばすと「押したのに紙が出来ない図面」に気づけない。
   */
  const stampQrMany = useMutation({
    mutationFn: ({ area, scalePercent }: { area: QrStampArea; scalePercent: number }) =>
      ops.stampQrMany!(targetIds, area, scalePercent),
    onSuccess: async (result) => {
      await invalidate();
      clearSelection();
      setChoosingQrArea(false);
      const skipped = [
        result.skipped_already_count && `${result.skipped_already_count} 件は押印済み`,
        result.skipped_unlinked_count &&
          `${result.skipped_unlinked_count} 件は明細未紐付け`,
        result.skipped_no_pdf_count && `${result.skipped_no_pdf_count} 件は PDF なし`,
      ].filter(Boolean);
      showSuccess(
        result.queued_count > 0
          ? `${result.queued_count} 件に QR を押しています。` +
              (skipped.length ? `（${skipped.join("、")}）` : "")
          : `押す図面がありませんでした。${skipped.join("、")}`,
      );
    },
    onError: (e: Error) => showError(e.message),
  });

  const saveNumber = async (
    drawing: PanelDrawing,
    drawingNumber: string,
  ): Promise<string | null> => {
    try {
      await ops.saveNumber(drawing.drawing_id, drawingNumber);
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  /**
   * 図番で明細を起こして紐付ける。
   *
   * 入力中の図番が保存前のことがあるので、違っていれば先に保存する。押す前に
   * 「図番を保存」も押させるのは手間が多い。
   */
  const createItem = async (
    drawing: PanelDrawing,
    drawingNumber: string,
  ): Promise<string | null> => {
    try {
      if (drawingNumber !== drawing.drawing_number) {
        await ops.saveNumber(drawing.drawing_id, drawingNumber);
      }
      await ops.createItem(drawing.drawing_id);
      await invalidate();
      showSuccess(`${drawingNumber} の明細を作って紐付けました。`);
      return null;
    } catch (e) {
      return errorMessage(e, "明細を作れませんでした。");
    }
  };

  /**
   * 選んだ図面から明細をまとめて起こす。
   *
   * 条件を満たさない図面（図番が空、既に紐付き済み）はサーバー側で飛ばされる。
   * **何枚飛ばしたかを必ず知らせる**。黙って減ると、押したのに増えていない理由が
   * 分からない。
   */
  const removeMany = useMutation({
    mutationFn: (ids: number[]) => ops.removeMany(ids),
    onSuccess: async (_data, ids) => {
      await invalidate();
      clearSelection();
      showSuccess(`${ids.length} 件の図面を削除しました。`);
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setBulkDeleting(false),
  });

  const link = useMutation({
    mutationFn: ({
      drawing,
      itemId,
    }: {
      drawing: PanelDrawing;
      itemId: number | null;
    }) => ops.link(drawing.drawing_id, itemId),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => showError(e.message),
  });

  const remove = useMutation({
    mutationFn: (drawing: PanelDrawing) => ops.remove(drawing.drawing_id),
    onSuccess: async () => {
      await invalidate();
      showSuccess("図面を削除しました。");
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => {
      setDeleting(null);
      setOpenedId(null);
    },
  });

  // 選んだ中に絵の出ていない図面があるときだけ作り直しを出す（普段は邪魔）。
  // 紐付無しの件数は絞り込みのチップが持つので、ここでは数えない。
  const missingThumbnails = selected.some(
    (d) => d.thumbnail_status === "failed" || d.thumbnail_status === "pending",
  );
  // いま後処理を待っている最中か。カードで「処理中」と「未処理」を出し分ける。
  // サーバが数えた残り件数で決める。画面側で時間を測らないので、何分かかっても
  // 「処理中」のままになり、終われば必ず解ける。
  const generating =
    pending !== null &&
    pending.pending_thumbnails + pending.pending_ocr + (pending.pending_qr ?? 0) > 0;

  /**
   * 後処理のバーに出す文言。**何を待っているのかを言い分ける。**
   *
   * 「処理中」とだけ出すと、1 枚 1.5 秒かかる図番の読み取りを待っているのか、
   * 11ms のサムネイルを待っているのかが読めない（42 枚だと 1 分ほど差が出る）。
   * 重いものから順に見る。
   */
  const generatingLabel = (() => {
    if (!pending) return null;
    if (pending.pending_ocr > 0)
      return `図番を読み取っています…（残り ${pending.pending_ocr} 枚）`;
    if ((pending.pending_qr ?? 0) > 0)
      return `QR を押しています…（残り ${pending.pending_qr} 枚）`;
    if (pending.pending_thumbnails > 0)
      return `サムネイルを作っています…（残り ${pending.pending_thumbnails} 枚）`;
    return null;
  })();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-3">
      {/* 最上段。**図面の有無にかかわらず必ず出す**ので、取り込みボタンの位置が
          動かない（絞り込みの行は図面があるときだけ出るので、ボタンを下の行に
          置いていたときは 0 件と 1 件以上で上下していた）。 */}
      <div className="flex min-h-9 flex-wrap items-center gap-2">
        {/* 絞り込み。件数を出すのは「残り何件か」を数えられるようにするため。 */}
        {rows.length > 0 &&
          FILTERS.map((f) => {
            const count = rows.filter(f.match).length;
            if (f.key !== "all" && count === 0) return null;
            return (
              <Chip
                key={f.key}
                size="sm"
                radius="sm"
                variant={filter === f.key ? "solid" : "bordered"}
                color={filter === f.key ? "primary" : "default"}
                className="cursor-pointer"
                data-testid={`drawing-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label} {count}
              </Chip>
            );
          })}

        <AuthorizedButton
          scope="transactional"
          radius="full"
          color="primary"
          className="ml-auto"
          data-testid="drawing-upload"
          startContent={<Upload size={18} />}
          isDisabled={!canWrite || uploading}
          isLoading={uploading}
          onPress={() => setImporting(true)}
        >
          図面をアップロード
        </AuthorizedButton>
      </div>

      {progress && (
        <Progress
          aria-label="取り込みの進み具合"
          size="sm"
          color="primary"
          value={(progress.done / progress.total) * 100}
          label={`取り込み中 ${progress.done} / ${progress.total}`}
        />
      )}

      {/* 取り込んだあとの後処理（サムネイル・図番・QR）。**終わるまで絵も図番も
          出ないので、何も起きていないように見える**のを防ぐ。残り件数はサーバが
          数えたものをそのまま出す（画面側で時間を測らない）。
          取り込み中は実測の進み具合がある上のバーを優先する。 */}
      {!progress && generating && generatingLabel && (
        <Progress
          aria-label="図面の後処理"
          size="sm"
          color="primary"
          isIndeterminate
          label={generatingLabel}
        />
      )}

      <div className="flex min-h-9 flex-wrap items-center gap-3">
        {canWrite && visible.length > 0 && (
          <Checkbox
            size="sm"
            isSelected={allSelected}
            isIndeterminate={selecting && !allSelected}
            onValueChange={toggleSelectAll}
            data-testid="drawing-select-all"
          >
            <span className="text-sm">すべて選択</span>
          </Checkbox>
        )}
        <span className="text-sm text-default-600">
          {visible.length} 件
          {filter !== "all" && (
            <span className="ml-1 text-default-400">/ 全 {rows.length} 件</span>
          )}
          {/* 選んだ件数は、押すボタンが何件に効くかを示す。 */}
          {selecting && (
            <span className="ml-2 font-bold text-brand-600">
              {selectedCount} 件を選択中
            </span>
          )}
        </span>

        {/* **操作は選んでから**。全件に効くボタンを常に出しておくと、取り込んだ
            直後の一覧で押し間違えたときの被害が大きい。選択は「すべて選択」で
            1 回押せば済む。 */}
        {/* **取り込みはボタン 1 つ。** 以前は全面のドロップ枠を出していたが、
            一覧の縦を常時 100px ほど使うわりに、使うのは取り込むときだけだった。 */}
        <div className="ml-auto flex items-center gap-2">
          {selecting && (
            <>
              {missingThumbnails && (
                <AuthorizedButton
                  scope="transactional"
                  size="sm"
                  radius="full"
                  variant="light"
                  data-testid="thumbnail-rebuild"
                  isLoading={rebuild.isPending}
                  onPress={() => rebuild.mutate()}
                >
                  サムネイルを作り直す
                </AuthorizedButton>
              )}
              <AuthorizedButton
                scope="transactional"
                size="sm"
                radius="full"
                variant="light"
                data-testid="drawing-ocr-reread"
                startContent={<ScanText size={16} />}
                isLoading={reread.isPending}
                onPress={() => reread.mutate()}
              >
                図番を読み直す
              </AuthorizedButton>
              {/* QR は案件の画面にだけある（見積は ops.stampQrMany を渡さない）。 */}
              {ops.stampQrMany && (
                <AuthorizedButton
                  scope="transactional"
                  size="sm"
                  radius="full"
                  variant="light"
                  data-testid="drawing-bulk-qr"
                  startContent={<QrCode size={16} />}
                  isLoading={stampQrMany.isPending}
                  onPress={() => setChoosingQrArea(true)}
                >
                  QR を押す
                </AuthorizedButton>
              )}
              <AuthorizedButton
                scope="transactional"
                size="sm"
                radius="full"
                variant="light"
                color="danger"
                data-testid="drawing-bulk-delete"
                startContent={<Trash2 size={16} />}
                isLoading={removeMany.isPending}
                onPress={() => setBulkDeleting(true)}
              >
                削除
              </AuthorizedButton>
              <AuthorizedButton
                scope="transactional"
                radius="full"
                color="primary"
                variant="bordered"
                data-testid="drawing-match"
                startContent={<Link2 size={18} />}
                onPress={() => setMatching(true)}
              >
                明細と紐づけ
              </AuthorizedButton>
            </>
          )}
        </div>
      </div>

      <DrawingImportDialog
        isOpen={importing}
        onClose={() => setImporting(false)}
        onSubmit={(files) => void upload(files)}
      />

      <div className="min-h-0 flex-1 overflow-auto">
        {drawings.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="読み込み中..." />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-default-500">
              図面がありません。「図面をアップロード」から PDF を取り込んでください。
            </p>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-default-500">
              「{activeFilter.label}」に当てはまる図面はありません。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 pb-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((drawing) => (
              <DrawingCard
                key={drawing.drawing_id}
                drawing={drawing}
                isGenerating={generating}
                isSelected={selectedIds.has(drawing.drawing_id)}
                selectable={canWrite}
                selecting={selecting}
                onToggleSelect={toggleSelect}
                onOpen={(d) => setOpenedId(d.drawing_id)}
                onOpenEditor={ops.openEditor}
                editorLabel={ops.editorLabel}
                linkedItem={
                  drawing.item_id == null
                    ? undefined
                    : items.find((i) => i.item_id === drawing.item_id)
                }
              />
            ))}
          </div>
        )}
      </div>

      <DrawingDetailDialog
        drawing={opened}
        items={linkableItems}
        canWrite={canWrite}
        linkHint={ops.linkHint}
        createItemHint={ops.createItemHint}
        onClose={() => setOpenedId(null)}
        onSaveNumber={saveNumber}
        onLink={(drawing, itemId) => link.mutate({ drawing, itemId })}
        onCreateItem={createItem}
        onDelete={setDeleting}
        // 差し替えるとサムネイルも版数も変わるので、一覧ごと取り直す。
        onRevisionsChanged={() => void invalidate()}
        onStampQr={ops.stampQr}
      />

      {/* 押す前に区画を選んでもらう。代表の絵は選択の先頭（全部に同じ区画で押す）。 */}
      <QrAreaDialog
        isOpen={choosingQrArea}
        drawing={selected[0] ?? null}
        targetCount={targetIds.length}
        isBusy={stampQrMany.isPending}
        onClose={() => setChoosingQrArea(false)}
        onConfirm={(area, scalePercent) => stampQrMany.mutate({ area, scalePercent })}
      />

      <DrawingMatchDialog
        isOpen={matching}
        ops={ops}
        drawingIds={targetIds}
        onClose={() => setMatching(false)}
        onDone={async (message) => {
          await invalidate();
          clearSelection();
          showSuccess(message);
        }}
      />

      <ConfirmDialog
        isOpen={bulkDeleting}
        title="選んだ図面の削除"
        message={`選んだ ${selectedCount} 件の図面を、登録済みの PDF ごと削除します。\nこの操作は取り消せません。よろしいですか？`}
        confirmLabel="削除する"
        isDanger
        isBusy={removeMany.isPending}
        onConfirm={() => removeMany.mutate(selected.map((d) => d.drawing_id))}
        onClose={() => setBulkDeleting(false)}
      />

      <ConfirmDialog
        isOpen={deleting != null}
        title="図面の削除"
        message={`${deleting?.drawing_number || "図番なし"} の図面を、登録済みの PDF ごと削除します。\nこの操作は取り消せません。よろしいですか？`}
        confirmLabel="削除する"
        isDanger
        isBusy={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
